// Status badges (Wither, Fortified, Garrisoned, Ascended ...) for any unit
// carrying `statuses` (the prop exists only while legends.enabled is on, so
// with the module off this renders nothing). A status kind the UI has no
// string for degrades to a humanised label.

import type { SceneNode } from '@aod/protocol';
import { type MessageKey, type Translate, useT } from '../i18n/index.js';
import { appDom } from '../lib/appDom.js';
import { KNOWN_STATUS_KINDS, humanizeKey, unitStatuses } from './championKinds.js';
import { ticksToSeconds } from './championRules.js';

const TONE: Readonly<Record<string, string>> = {
  wither: 'bg-violet-500/20 text-violet-800 dark:text-violet-300',
  fortify: 'bg-stone-500/25 text-stone-800 dark:text-stone-300',
  garrison: 'bg-stone-500/25 text-stone-800 dark:text-stone-300',
  ascended: 'bg-amber-500/25 text-amber-800 dark:text-amber-300',
};
const DEFAULT_TONE = 'bg-muted text-muted-foreground';

export function statusLabel(t: Translate, kind: string): string {
  return KNOWN_STATUS_KINDS.has(kind)
    ? t(`champion.status.${kind}` as MessageKey)
    : humanizeKey(kind);
}

/** Tooltip: what the status does, plus the time left when it expires. */
export function statusTitle(
  t: Translate,
  s: { kind: string; mag: number; untilTick: number | null },
  tick: number | null,
): string {
  const hint = KNOWN_STATUS_KINDS.has(s.kind)
    ? t(`champion.status.${s.kind}Hint` as MessageKey, { mag: s.mag })
    : '';
  const left =
    typeof s.untilTick === 'number' && tick !== null
      ? t('champion.status.left', { seconds: ticksToSeconds(Math.max(0, s.untilTick - tick)) })
      : '';
  return [hint, left].filter(Boolean).join(' · ');
}

export function UnitStatusBadges({ unit, tick }: { unit: SceneNode; tick: number | null }) {
  const t = useT();
  const statuses = unitStatuses(unit);
  if (statuses.length === 0) return null;
  return (
    <ul
      data-testid="unit-statuses"
      {...appDom({
        id: 'unit-statuses',
        type: 'List',
        scope: 'aside',
        label: t('champion.status.title'),
      })}
      aria-label={t('champion.status.title')}
      className="flex flex-wrap gap-1"
    >
      {statuses.map((s) => (
        <li
          key={s.kind}
          data-testid={`unit-status-${s.kind}`}
          data-status={s.kind}
          title={statusTitle(t, s, tick)}
          className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${TONE[s.kind] ?? DEFAULT_TONE}`}
        >
          {statusLabel(t, s.kind)}
          {typeof s.untilTick === 'number' && tick !== null && (
            <span className="ml-1 font-normal tabular-nums opacity-80">
              {ticksToSeconds(Math.max(0, s.untilTick - tick))}s
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}
